<template>
  <div class="contas-list">
    <div class="mb-2 d-flex justify-content-between align-items-center">
      <h2>Contas</h2>
      <b-button variant="success" @click="addNew()">
        <feather-icon icon="PlusIcon" class="mr-50" />
        Nova Conta
      </b-button>
    </div>

    <b-card class="mb-2">
      <b-row>
        <b-col md="10">
          <b-form-group label="Buscar Conta">
            <b-form-input
              v-model="searchTerm"
              placeholder="Buscar por nome, banco, agência ou conta..."
              @input="handleSearch"
            />
          </b-form-group>
        </b-col>
        <b-col md="2" class="d-flex align-items-end mb-1">
          <b-button variant="primary" block @click="performSearch">
            <feather-icon icon="SearchIcon" class="mr-50" />
            Pesquisar
          </b-button>
        </b-col>
      </b-row>
    </b-card>

    <!-- Exibição de loading se necessário -->
    <div v-if="isLoading" class="text-center my-3">
      <b-spinner variant="primary" label="Carregando..." />
    </div>

    <b-row v-else class="match-height">
      <b-col
        v-for="item in processedContas"
        :key="item.id"
        xl="4"
        lg="6"
        md="6"
        class="mb-2"
      >
        <b-card :class="{ 'border-primary': item.principal }">
          <div class="d-flex align-items-start justify-content-between mb-1">
            <div>
              <div class="d-flex align-items-center gap-2 mb-1">
                <h4 class="mb-0 mr-1">{{ item.descricao }}</h4>
                <b-badge v-if="item.principal" variant="primary">Principal</b-badge>
              </div>
              <p class="text-secondary mb-0">
                <feather-icon v-if="!item.banco" icon="BriefcaseIcon" class="mr-25" />
                <span v-if="item.banco">
                  {{ item.banco.nome }}
                  <span v-if="item.agencia"> - Ag. {{ item.agencia }}</span>
                  <span v-if="item.conta">
                    - C/C {{ item.conta }}<span v-if="item.digitoVerificador">-{{ item.digitoVerificador }}</span>
                  </span>
                </span>
                <span v-else>
                  {{ item.tipoConta ? item.tipoConta.nome : 'Conta Caixa' }}
                </span>
              </p>
            </div>
            <div class="avatar p-50 bg-light-secondary rounded">
              <feather-icon
                icon="BriefcaseIcon"
                size="24"
                :class="item.valorSaldoAtual >= 0 ? 'text-success' : 'text-danger'"
              />
            </div>
          </div>

          <div class="mb-2">
            <div class="d-flex justify-content-between align-items-center mb-50">
              <span class="text-secondary">Saldo Atual</span>
              <span class="font-weight-bold font-medium-3" :class="item.valorSaldoAtual >= 0 ? 'text-success' : 'text-danger'">
                {{ formatCurrencyBrazil(item.valorSaldoAtual) }}
              </span>
            </div>
            <small v-if="item.moeda !== 'BRL'" class="text-secondary">Moeda: {{ item.moeda }}</small>
          </div>

          <div class="d-flex flex-column gap-2 mt-2">
            <b-row class="mb-1">
              <b-col sm="6">
                <b-button variant="outline-info" block size="sm" @click="viewDetail(item)">
                  <feather-icon icon="EyeIcon" class="mr-25" />
                  Detalhes
                </b-button>
              </b-col>
              <b-col sm="6">
                <b-button variant="outline-warning" block size="sm" @click="edit(item)">
                  <feather-icon icon="EditIcon" class="mr-25" />
                  Editar
                </b-button>
              </b-col>
            </b-row>
            <b-row class="mb-1">
              <b-col sm="6">
                <b-button variant="outline-primary" block size="sm" @click="viewTransactions(item)">
                  <feather-icon icon="ListIcon" class="mr-25" />
                  Transações
                </b-button>
              </b-col>
              <b-col sm="6">
                <b-button variant="outline-secondary" block size="sm" @click="ajustarSaldo(item)">
                  <feather-icon icon="DollarSignIcon" class="mr-25" />
                  Ajustar Saldo
                </b-button>
              </b-col>
            </b-row>
          </div>

          <template #footer v-if="item.descricao">
            <small class="text-secondary">{{ truncate(item.descricao, 100) }}</small>
          </template>
        </b-card>
      </b-col>

      <b-col v-if="processedContas.length === 0" cols="12">
        <b-card class="text-center py-5">
          <feather-icon icon="InboxIcon" size="48" class="text-secondary mb-1" />
          <h4 class="mb-1">Nenhuma Conta Cadastrada</h4>
          <p class="text-secondary mb-2">
            Cadastre contas bancárias ou caixas para começar a gerenciar suas finanças.
          </p>
          <b-button variant="success" @click="addNew()">
            Nova Conta
          </b-button>
        </b-card>
      </b-col>
    </b-row>

    <!-- Pagination -->
    <div class="d-flex justify-content-between flex-wrap mt-2" v-if="tableConfigs.totalElements > 0">
      <div class="d-flex align-items-center mb-0 mt-1">
        <span class="text-nowrap"> Mostrando </span>
        <b-form-select
          v-model="tableConfigs.size"
          :options="[10, 20, 50]"
          class="mx-1"
          @input="handleChangePage(1)"
        />
        <span class="text-nowrap">
          de {{ tableConfigs.totalElements }} registros
        </span>
      </div>
      <div>
        <b-pagination
          v-model="currentPage"
          :total-rows="tableConfigs.totalElements"
          :per-page="tableConfigs.size"
          first-number
          last-number
          align="right"
          prev-class="prev-item"
          next-class="next-item"
          class="mt-1 mb-0"
          @change="handleChangePage"
        >
          <template #prev-text>
            <feather-icon icon="ChevronLeftIcon" size="18" />
          </template>
          <template #next-text>
            <feather-icon icon="ChevronRightIcon" size="18" />
          </template>
        </b-pagination>
      </div>
    </div>
  </div>
</template>

<script>
import {
  BRow, BCol, BCard, BButton, BFormGroup, BFormInput, BBadge, BSpinner, BPagination, BFormSelect
} from 'bootstrap-vue'
import { mapState } from 'vuex'
import _ from 'lodash'
import mixinsGlobal from '@/mixins'

export default {
  name: 'ContasList',
  components: {
    BRow, BCol, BCard, BButton, BFormGroup, BFormInput, BBadge, BSpinner, BPagination, BFormSelect
  },
  mixins: [mixinsGlobal],
  data() {
    return {
      searchTerm: '',
      isLoading: false,
      currentPage: 1,
      pageConfig: {
        page: 1, // 1-indexed for b-pagination coupling
        size: 10,
        sort: 'descricao',
        order: 'asc'
      },
      tableConfigs: {
        totalPages: 0,
        totalElements: 0,
        size: 10,
        number: 0
      }
    }
  },
  computed: {
    ...mapState('contaFinancialModule', {
      listContas: state => state.listContas
    }),
    processedContas() {
      return this.listContas || []
    }
  },
  mounted() {
    this.loadData()
  },
  methods: {
    truncate(text, length) {
      if (!text) return ''
      return text.length > length ? text.substring(0, length) + '...' : text
    },
    async loadData() {
      this.isLoading = true
      try {
        let result
        if (this.searchTerm && this.searchTerm.trim() !== '') {
          result = await this.$store.dispatch('contaFinancialModule/find', {
            nome: this.searchTerm,
            pageConfig: this.pageConfig
          })
        } else {
          result = await this.$store.dispatch('contaFinancialModule/findAll', this.pageConfig)
        }
        
        if (result && result.data) {
          this.tableConfigs.totalPages = result.data.totalPages
          this.tableConfigs.totalElements = result.data.totalElements
          this.tableConfigs.size = result.data.size
          this.tableConfigs.number = result.data.number
        }
      } catch (error) {
        this.MensagemError('Erro', 'Falha ao buscar as contas')
      } finally {
        this.isLoading = false
      }
    },
    handleSearch: _.debounce(function () {
      this.pageConfig.page = 1
      this.currentPage = 1
      this.loadData()
    }, 500),
    performSearch() {
      this.pageConfig.page = 1
      this.currentPage = 1
      this.loadData()
    },
    handleChangePage(page) {
      this.pageConfig.page = page
      this.loadData()
    },
    addNew() {
      this.$store.dispatch('contaFinancialModule/reset')
      this.$router.push({ name: 'conta-form' })
    },
    edit(item) {
      this.$store.dispatch('contaFinancialModule/prepareToEdit', item)
      this.$router.push({ name: 'conta-edit', params: { id: item.id } })
    },
    viewDetail(item) {
      this.$router.push({ name: 'conta-detail', params: { id: item.id } })
    },
    viewTransactions(item) {
      this.$router.push({ name: 'transacoesFinanceiras', query: { contaId: String(item.id) } })
    },
    ajustarSaldo(item) {
      this.$router.push({ name: 'ajuste-conta-form', query: { contaId: String(item.id) } })
    }
  }
}
</script>

<style lang="scss" scoped>
.gap-2 {
  gap: 0.5rem;
}
.match-height > [class*='col'] {
  display: flex;
  flex-flow: column;
}
.match-height > [class*='col'] > .card {
  flex: 1 1 auto;
}
</style>
